// ─────────────────────────────────────────────────────────────────────────────
// ProductsPage.jsx
// MIAVIT-style product listing with filters + 4-col grid
//
// Filters:
//   - Animal Species (Poultry / Dairy / Multi)
//   - Product Category (from categories.js)
//   - Supplier / Brand (from suppliers.js)  ← client requested
//
// URL params: ?species=poultry&category=gut-health&supplier=miavit
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from 'react'
import { useSearchParams }              from 'react-router-dom'
import { products }                     from '@data/products'
import { productCategories, sectors }   from '@data/categories'
import { suppliers }                    from '@data/suppliers'
import PageHero                         from '@components/common/PageHero'
import SEOHead                          from '@components/common/SEOHead'

// ── Fade-in hook ──────────────────────────────────────────────────────────────
function useFadeIn(threshold = 0.05) {
  const ref = useRef(null)
  const [vis, setVis] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true) },
      { threshold }
    )
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, vis]
}

// ── Gradient fallbacks ────────────────────────────────────────────────────────
const BG = [
  'linear-gradient(135deg, #2d5c3e 0%, #3d7a55 100%)',
  'linear-gradient(135deg, #1e4d2f 0%, #2d6040 100%)',
  'linear-gradient(135deg, #163d25 0%, #255038 100%)',
  'linear-gradient(135deg, #0f3020 0%, #1e4d2f 100%)',
]

// ── Product Card — MIAVIT style ───────────────────────────────────────────────
function ProductCard({ product, index, triggered }) {
  const [hovered, setHovered] = useState(false)
  const supplier = suppliers.find(s => s.id === product.supplier)

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:       'flex',
        flexDirection: 'column',
        opacity:       triggered ? 1 : 0,
        transform:     triggered ? 'translateY(0)' : 'translateY(20px)',
        transition:    `opacity 0.4s ease ${(index % 8) * 0.06}s,
                        transform 0.4s ease ${(index % 8) * 0.06}s`,
      }}
    >
      {/* Image with name overlay — MIAVIT exact style */}
      <div style={{
        position:    'relative',
        aspectRatio: '4/3',
        overflow:    'hidden',
        background:  product.image ? 'transparent' : BG[index % BG.length],
        flexShrink:  0,
      }}>
        {product.image && (
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            style={{
              width:      '100%', height: '100%',
              objectFit:  'cover', display: 'block',
              transform:  hovered ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 0.4s ease',
            }}
          />
        )}

        {/* Name strip at bottom — product detail navigation hidden until confirmed */}
        <div
          style={{
            position:   'absolute', bottom: 0, left: 0, right: 0,
            display:    'flex', alignItems: 'center',
            padding:    '10px 14px',
            background: hovered ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.58)',
            fontFamily: "'DM Sans', sans-serif",
            fontSize:   '13.5px', fontWeight: 500,
            color:      '#ffffff',
            transition: 'background 0.2s ease',
          }}
        >
          <span>{product.name}</span>
        </div>
      </div>

      {/* Content below image */}
      <div style={{ paddingTop: '14px', flex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Tagline */}
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize:   '13px', color: '#55544e',
          lineHeight: 1.65, margin: '0 0 12px', flex: 1,
        }}>
          {product.tagline}
        </p>

        {/* Supplier tag + WhatsApp button — same row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>

          {supplier ? (
            <span style={{
              display:       'inline-flex', alignItems: 'center', gap: '5px',
              fontFamily:    "'DM Sans', sans-serif",
              fontSize:      '10px', fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color:         '#007935',
              background:    '#e8f5ee',
              border:        '1px solid #b8dfc7',
              padding:       '3px 9px',
              whiteSpace:    'nowrap',
            }}>
              {supplier.name} · {supplier.country}
            </span>
          ) : <span />}

          {/* Contact for more — opens WhatsApp with product name */}
          <a
            href={`https://wa.me/94718919034?text=${encodeURIComponent(`Hello, I would like to know more about ${product.name}.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display:        'inline-flex', alignItems: 'center', gap: '6px',
              fontFamily:     "'DM Sans', sans-serif",
              fontSize:       '12.5px', fontWeight: 600,
              color:          hovered ? '#ffffff' : '#007935',
              textDecoration: 'none',
              padding:        '6px 13px',
              border:         '1.5px solid #007935',
              background:     hovered ? '#007935' : 'transparent',
              transition:     'all 0.2s ease',
              whiteSpace:     'nowrap',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Contact for more
          </a>

        </div>
      </div>
    </div>
  )
}

// ── Checkbox item ─────────────────────────────────────────────────────────────
function CheckItem({ label, checked, onChange }) {
  return (
    <label style={{
      display:    'flex', alignItems: 'center', gap: '8px',
      cursor:     'pointer', marginBottom: '8px',
      fontFamily: "'DM Sans', sans-serif",
      fontSize:   '13.5px', color: '#44443e',
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ width: '15px', height: '15px', accentColor: '#007935', cursor: 'pointer', flexShrink: 0 }}
      />
      {label}
    </label>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [gridRef, gridVis] = useFadeIn()

  // Filter state — read from URL params
  const [selectedSpecies,   setSelectedSpecies]   = useState(() => {
    const p = searchParams.get('species'); return p ? [p] : []
  })
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const p = searchParams.get('category'); return p ? [p] : []
  })
  const [selectedSuppliers,  setSelectedSuppliers]  = useState(() => {
    const p = searchParams.get('supplier'); return p ? [p] : []
  })

  // Toggle helper
  const toggle = (arr, setArr, val) => {
    setArr(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    )
  }

  // Filter products
  const filtered = products.filter(p => {
    const speciesMatch = selectedSpecies.length === 0 ||
      p.sectors.some(s => selectedSpecies.map(x => x.toLowerCase()).includes(s.toLowerCase()))
    const categoryMatch = selectedCategories.length === 0 ||
      selectedCategories.includes(p.category)
    const supplierMatch = selectedSuppliers.length === 0 ||
      selectedSuppliers.includes(p.supplier)
    return speciesMatch && categoryMatch && supplierMatch
  })

  const hasFilters = selectedSpecies.length > 0 || selectedCategories.length > 0 || selectedSuppliers.length > 0

  const clearAll = () => {
    setSelectedSpecies([])
    setSelectedCategories([])
    setSelectedSuppliers([])
  }

  return (
    <main>
      <SEOHead
        title="Products"
        description="Browse Salient Agrinutrition's range of animal health and nutrition products — probiotics, vitamins, minerals, gut health, and respiratory health solutions for poultry and dairy."
        path="/products"
      />

      {/* ── PageHero ── */}
      <PageHero
        title="Products"
        subtitle="Science-driven nutritional solutions for poultry and dairy production systems."
        breadcrumbs={[
          { label: 'Home',     to: '/' },
          { label: 'Products' },
        ]}
      />

      {/* ── Filter + Grid ── */}
      <section style={{ background: '#ffffff', padding: '56px 0 72px' }}>
        <div style={{
          maxWidth: 'var(--container-max)', margin: '0 auto',
          padding:  '0 var(--container-padding)',
        }}>

          {/* ── 3-col Filter — MIAVIT style ── */}
          <div
            className="filter-grid"
            style={{
              display:             'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap:                 '40px',
              padding:             '28px 32px',
              background:          '#f9f9f7',
              border:              '1px solid #e8e8e8',
              marginBottom:        '40px',
            }}
          >
            {/* Col 1: Animal Species */}
            <div>
              <p style={{
                fontFamily:    "'DM Sans', sans-serif",
                fontSize:      '11px', fontWeight: 700,
                letterSpacing: '0.10em', textTransform: 'uppercase',
                color:         '#007935', margin: '0 0 14px',
              }}>
                Animal Species
              </p>
              {sectors.map(s => (
                <CheckItem
                  key={s.id}
                  label={s.label}
                  checked={selectedSpecies.includes(s.id)}
                  onChange={() => toggle(selectedSpecies, setSelectedSpecies, s.id)}
                />
              ))}
            </div>

            {/* Col 2: Product Category */}
            <div>
              <p style={{
                fontFamily:    "'DM Sans', sans-serif",
                fontSize:      '11px', fontWeight: 700,
                letterSpacing: '0.10em', textTransform: 'uppercase',
                color:         '#007935', margin: '0 0 14px',
              }}>
                Product Category
              </p>
              {productCategories.map(c => (
                <CheckItem
                  key={c.id}
                  label={c.label}
                  checked={selectedCategories.includes(c.id)}
                  onChange={() => toggle(selectedCategories, setSelectedCategories, c.id)}
                />
              ))}
            </div>

            {/* Col 3: Supplier / Brand — client requested */}
            <div>
              <p style={{
                fontFamily:    "'DM Sans', sans-serif",
                fontSize:      '11px', fontWeight: 700,
                letterSpacing: '0.10em', textTransform: 'uppercase',
                color:         '#007935', margin: '0 0 14px',
              }}>
                Supplier / Brand
              </p>
              {suppliers.map(s => (
                <CheckItem
                  key={s.id}
                  label={`${s.name} (${s.country})`}
                  checked={selectedSuppliers.includes(s.id)}
                  onChange={() => toggle(selectedSuppliers, setSelectedSuppliers, s.id)}
                />
              ))}
            </div>
          </div>

          {/* Filter actions row */}
          <div style={{
            display:         'flex', alignItems: 'center',
            justifyContent:  'space-between', flexWrap: 'wrap',
            gap:             '12px', marginBottom: '32px',
          }}>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize:   '13.5px', color: '#88877f', margin: 0,
            }}>
              Showing <strong style={{ color: '#1a1a18' }}>{filtered.length}</strong> of{' '}
              <strong style={{ color: '#1a1a18' }}>{products.length}</strong> products
            </p>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {hasFilters && (
                <button
                  onClick={clearAll}
                  style={{
                    fontFamily:     "'DM Sans', sans-serif",
                    fontSize:       '13px', fontWeight: 600,
                    color:          '#88877f', background: 'transparent',
                    border:         '1.5px solid #e0dfda',
                    padding:        '7px 16px', cursor: 'pointer',
                    transition:     'all 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#007935'; e.currentTarget.style.color = '#007935' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0dfda'; e.currentTarget.style.color = '#88877f' }}
                >
                  Clear all filters
                </button>
              )}
            </div>
          </div>

          {/* ── 4-col product grid ── */}
          {filtered.length > 0 ? (
            <div
              ref={gridRef}
              className="product-grid"
              style={{
                display:             'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap:                 '32px 24px',
                alignItems:          'start',
              }}
            >
              {filtered.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} triggered={gridVis} />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize:   '15px', color: '#88877f', margin: '0 0 16px',
              }}>
                No products match the selected filters.
              </p>
              <button
                onClick={clearAll}
                style={{
                  fontFamily:  "'DM Sans', sans-serif",
                  fontSize:    '13.5px', fontWeight: 600,
                  color:       '#ffffff', background: '#007935',
                  border:      'none', padding: '9px 22px', cursor: 'pointer',
                }}
              >
                Clear filters
              </button>
            </div>
          )}

        </div>
      </section>

      <style>{`
        @media (max-width: 1100px) { .product-grid { grid-template-columns: repeat(3, 1fr) !important; } }
        @media (max-width: 767px)  { .product-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 20px 14px !important; } }
        @media (max-width: 480px)  { .product-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 767px)  { .filter-grid  { grid-template-columns: 1fr !important; gap: 24px !important; } }
        @media (min-width: 768px) and (max-width: 1023px) { .filter-grid { grid-template-columns: 1fr 1fr !important; } }
      `}</style>
    </main>
  )
}